export const AUTH_RETURN_TO_STORAGE_KEY = 'auth:returnTo'
export const APP_BASENAME = '/aura'

const GENERIC_AUTH_PATHS = new Set([
	'/',
	APP_BASENAME,
	`${APP_BASENAME}/`,
	`${APP_BASENAME}/home`,
	'/home',
])

const getBaseOrigin = () =>
	typeof window !== 'undefined' ? window.location.origin : 'http://localhost'

const getUrl = (value: string) => new URL(value, getBaseOrigin())

const ensureBrowserAppPath = (path: string) => {
	if (!path.startsWith('/')) return path
	if (path === APP_BASENAME || path.startsWith(`${APP_BASENAME}/`)) return path
	if (path === '/') return APP_BASENAME

	return `${APP_BASENAME}${path}`
}

export const normalizeReturnTo = (returnTo?: null | string): null | string => {
	if (!returnTo || typeof returnTo !== 'string') return null

	try {
		const url = getUrl(returnTo)

		if (url.origin !== getBaseOrigin()) {
			return null
		}

		return ensureBrowserAppPath(`${url.pathname}${url.search}${url.hash}`)
	} catch {
		const normalized = returnTo.startsWith('/')
			? returnTo
			: `/${returnTo.replace(/^\/+/, '')}`

		return ensureBrowserAppPath(normalized)
	}
}

const getDefaultReturnToPath = () =>
	normalizeReturnTo(process.env.REACT_APP_AUTH0_REDIRECT_URI) ||
	`${APP_BASENAME}/home`

/** BFF auth expects a full frontend URL (origin + /aura path). */
export const toAbsoluteReturnTo = (returnTo?: null | string): string => {
	const path = normalizeReturnTo(returnTo) || getDefaultReturnToPath()

	if (path.startsWith('http://') || path.startsWith('https://')) {
		return path
	}

	return `${getBaseOrigin()}${path}`
}

export const getCurrentReturnTo = () =>
	toAbsoluteReturnTo(
		typeof window !== 'undefined' ? window.location.href : undefined,
	)

export const isGenericAuthUrl = (url?: null | string): boolean => {
	const normalized = normalizeReturnTo(url)
	if (!normalized) return true

	const [pathname] = normalized.split(/[?#]/)
	return GENERIC_AUTH_PATHS.has(pathname)
}

const readRememberedReturnTo = (): null | string => {
	try {
		return sessionStorage.getItem(AUTH_RETURN_TO_STORAGE_KEY)
	} catch {
		return null
	}
}

export const rememberReturnTo = (returnTo: string): null | string => {
	const normalized = normalizeReturnTo(returnTo)
	if (!normalized) return null

	try {
		sessionStorage.setItem(AUTH_RETURN_TO_STORAGE_KEY, normalized)
	} catch {
		// Ignore storage failures and fall back to appState only.
	}

	return normalized
}

export const resolveReturnToForLogin = (currentUrl: string): string => {
	const remembered = readRememberedReturnTo()

	if (remembered && isGenericAuthUrl(currentUrl)) {
		return remembered
	}

	return rememberReturnTo(currentUrl) || '/aura/home'
}

export const toRouterPath = (returnTo?: null | string): string => {
	const normalized = normalizeReturnTo(returnTo)

	if (!normalized) return '/home'
	if (normalized === APP_BASENAME) return '/'
	if (normalized.startsWith(`${APP_BASENAME}/`)) {
		return normalized.slice(APP_BASENAME.length) || '/'
	}

	return normalized
}
